import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {HeadlineSectionSliceHighlightsPrimary} from 'prismicio-types'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {Gradient} from 'slices/ProductOverview/components/separators'
import {Container} from 'src/common/Container'
import type {IconType} from 'src/common/Icon'
import {Icon} from 'src/common/Icon'
import {getIconName} from 'src/common/helpers'
import {sanitizeHtml} from 'src/common/helpers/sanitizeHtml'

type HighlightsHeadlineSectionProps = {
  slice: {
    primary: HeadlineSectionSliceHighlightsPrimary
    slice_type: string
    variation: string
  }
}

const GRID_3_COLS =
  'grid-cols-[20px_1fr_20px] md:grid-cols-[40px_1fr_1fr_1fr_40px] lg:grid-cols-[20px_minmax(0,401px)_minmax(0,401px)_minmax(0,385px)_20px] xl:grid-cols-[1fr_minmax(0,401px)_minmax(0,401px)_minmax(0,401px)_1fr]'

const GRID_ROWS = 'grid-rows-[32px_auto_auto_auto_32px] md:grid-rows-[64px_minmax(160px,auto)_64px]'

const COLOR_MAP = {
  'Beige/200': '242,244,239',
  'Beige/100': '247,248,245',
  'Moss White': '255,255,255',
}

/**
 * Component for "HeadlineSection" Slices.
 */
export const HighlightsHeadlineSection = ({
  slice: {
    primary: {eyebrow, highlights, background_color, title, subtext_rte},
    variation,
    slice_type,
  },
}: HighlightsHeadlineSectionProps): JSX.Element => {
  const sanitizedTitle = sanitizeHtml(title!)
  const gradientColor = COLOR_MAP[background_color as keyof typeof COLOR_MAP] || '255,255,255'

  return (
    <Container
      {...(slice_type && {'data-slice-type': slice_type})}
      {...(variation && {'data-slice-variation': variation})}
      backgroundColor={background_color}
      className="flex flex-col items-center !pb-0 !pt-12 md:!pt-[96px]"
    >
      <div className="w-full max-w-[1200px] gap-y-5 px-5 md:px-10 lg:gap-y-16 lg:px-5 xl:px-0">
        <div className="flex flex-col gap-y-6 max-md:pb-5 max-md:pr-[28px] md:max-w-[550px]">
          {eyebrow && <p className="text-button-s col-span-4 text-moss-blue">{eyebrow}</p>}
          <h1
            className="text-tg-h2 md:text-h1 col-span-full font-medium text-moss-black max-md:leading-[normal] md:tracking-normal"
            dangerouslySetInnerHTML={{__html: sanitizedTitle}}
          />
          <PrismicRichText
            components={{
              paragraph: ({children}) => (
                <p className="text-tg-body-l col-span-4 text-moss-grey-550 md:col-span-8 md:max-w-[360px] lg:col-span-5 ">
                  {children}
                </p>
              ),
            }}
            field={subtext_rte}
          />
        </div>
      </div>
      <div
        className={`
          relative
          grid
          w-full
          ${GRID_3_COLS}
          ${GRID_ROWS}
        `}
      >
        <Gradient location="top" color={gradientColor} gradientStart={10} height={30} />
        {isFilled.group(highlights) &&
          highlights.map((highlight, index) => {
            const iconName = getIconName(highlight.icon)

            return (
              <div
                className="col-span-1 ml-[-1px] mt-[-1px] flex flex-col gap-4 rounded-[4px] border border-moss-grey-150 p-6 md:justify-between"
                key={highlight.title! + index}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    name={iconName as IconType}
                    className={cn('text-neon-550', {
                      'h-4 w-4 md:h-8 lg:w-8': iconName === 'plus-bold',
                      'h-[18px] w-[18px] lg:h-[23px] lg:w-[23px]': iconName !== 'plus-bold',
                    })}
                  />
                  <h3 className="text-tg-h2 lg:text-tg-h1 text-green-dark max-md:leading-[normal] md:text-start">
                    {highlight.title}
                  </h3>
                </div>
                <p className="text-detail uppercase text-black md:text-start">{highlight.subtext}</p>
              </div>
            )
          })}
        <Separators />
        <Gradient location="bottom" color={gradientColor} gradientStart={10} height={30} />
      </div>
    </Container>
  )
}

const LEFT_SEPARATOR_CLASSES = 'mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150'
const LEFT_TOP_SEPARATOR_CLASSES = 'rounded-br-[4px] border-b border-r border-moss-grey-150'
const LEFT_BOTTOM_SEPARATOR_CLASSES = 'mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150'

const RIGHT_SEPARATOR_CLASSES = 'ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150'
const RIGHT_TOP_SEPARATOR_CLASSES = 'ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150'
const RIGHT_BOTTOM_SEPARATOR_CLASSES = 'ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150'

export const Separators = () => (
  <>
    {/* Left */}
    <div className={`col-span-1 col-start-1 row-start-1 ${LEFT_TOP_SEPARATOR_CLASSES}`} />
    <div className={`col-span-1 col-start-1 row-start-2 max-md:row-end-5 ${LEFT_SEPARATOR_CLASSES}`} />
    <div className={`col-span-1 col-start-1 row-start-5 md:row-start-3 ${LEFT_BOTTOM_SEPARATOR_CLASSES}`} />

    {/* Center */}
    <div className="col-span-1 col-start-2 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150" />
    <div className="col-span-1 col-start-3 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:hidden" />
    <div className="col-span-1 col-start-4 row-start-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:hidden" />
    <div className="col-span-1 col-start-2 row-start-5 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 md:row-start-3" />
    <div className="col-span-1 col-start-3 row-start-5 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:hidden md:row-start-3" />
    <div className="col-span-1 col-start-4 row-start-5 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:hidden md:row-start-3" />

    {/* Right */}
    <div className={`col-span-1 col-start-3 row-start-1 md:col-start-5 ${RIGHT_TOP_SEPARATOR_CLASSES}`} />
    <div className={`col-span-1 col-start-3 row-start-2 max-md:row-end-5 md:col-start-5 ${RIGHT_SEPARATOR_CLASSES}`} />
    <div
      className={`col-span-1 col-start-3 row-start-5 md:col-start-5 md:row-start-3 ${RIGHT_BOTTOM_SEPARATOR_CLASSES}`}
    />
  </>
)
